interface PlayerErrorOptions {
  sessionId: string;
  position: number | undefined;
  cause?: unknown;
}

export class PlayerError extends Error {
  public readonly title: string;
  public readonly message: string;
  public readonly sessionId: string;
  public readonly position: number | undefined;
  public readonly cause?: unknown;

  constructor(title: string, message: string, options: PlayerErrorOptions) {
    super(message);
    this.title = title;
    this.message = message;
    this.sessionId = options.sessionId;
    this.position = options.position;
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export interface ShakaErrorEvent extends Event {
  type: 'error';
  timestamp: number;
  detail: ShakaErrorDetail;
}

export interface BaseShakaErrorDetail {
  category: number;
  code: number;
  data: unknown[];
  handled: boolean;
  severity: number;
}

export interface BadHTTPStatusCodeErrorDetail extends BaseShakaErrorDetail {
  code: 1001;
  data: [number, string];
}

export interface HTTPErrorDetail extends BaseShakaErrorDetail {
  code: 1002;
}

export interface TimeoutErrorDetail extends BaseShakaErrorDetail {
  code: 1003;
}

/**
 * The video element reported an error.
 * - error.data[0] is a MediaError code from the video element.
 * - On Edge, error.data[1] is a Microsoft extended error code in hex.
 * - On Chrome, error.data[2] is a string with details on the error.
 */
export interface VideoErrorDetail extends BaseShakaErrorDetail {
  code: 3016;
  data: [mediaErrorCode: number, error: MediaError | undefined, message: string];
}

export interface RestrictionsCannotBeMetErrorDetail extends BaseShakaErrorDetail {
  code: 4012;
  data: [shaka.extern.RestrictionInfo];
}

export interface ManifestParsingErrorDetail extends BaseShakaErrorDetail {
  code: 6010;
}

export type ShakaErrorDetail =
  | BadHTTPStatusCodeErrorDetail
  | HTTPErrorDetail
  | TimeoutErrorDetail
  | VideoErrorDetail
  | RestrictionsCannotBeMetErrorDetail
  | ManifestParsingErrorDetail;

export class ShakaError extends PlayerError {
  constructor(detail: ShakaErrorDetail, options: PlayerErrorOptions) {
    const [title, message] = shakaErrorMapper(detail);
    super(title, message, options);
  }
}

export function isShakaError(error: unknown): error is { detail: ShakaErrorDetail } {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  return 'detail' in error;
}

export function shakaErrorMapper(detail: ShakaErrorDetail): [title: string, message: string] {
  switch (detail.code) {
    case 1001:
      return ['Bad HTTP status code', `The server responded with HTTP status code ${detail.data[1]}.`];
    case 1002:
      return ['HTTP error', 'An HTTP network request failed with an error, but not from the server.'];
    case 1003:
      return ['Timeout', 'A network request timed out.'];
    case 3016:
      return ['Video error', detail.data[2] ? detail.data[2] : 'The video element reported an error.'];
    case 4012:
      return [
        'Restrictions cannot be met',
        `The video cannot be played because the following restrictions cannot be met: ${detail.data[0].toString()}`,
      ];
    case 6010:
      return ['Manifest parsing error', 'The manifest does not specify any DRM info, but the content is encrypted.'];
    default:
      // @ts-expect-error
      return ['Shaka error', `An error occured (${detail.severity}:${detail.code}).`];
  }
}
